/**
 * The links that send a reader from the web app to the Music24 mobile apps.
 * Contract: docs/superpowers/plans/2026-09-29-music24-mobile-app-handoff.md.
 */

export const IOS_APP_STORE_URL = 'https://apps.apple.com/app/id1629444203';
export const ANDROID_PACKAGE = 'com.smartlinksapp.app';

/**
 * The app path that opens Catalog on the connect of one source (design your-intelligence h4). The
 * old path `/app/insights?connect=spotify-for-artists` redirects here on the web.
 */
export function catalogConnectPath(service: string): string {
  return `/app/catalog/connect/${encodeURIComponent(service)}`;
}

/** The app path that opens Catalog and starts the Spotify for Artists connect. */
export const SPOTIFY_FOR_ARTISTS_CONNECT_PATH = catalogConnectPath('spotify-artists');

/** The app path that opens Catalog and starts the Ditto Music connect (design your-intelligence k2). */
export const DITTO_CONNECT_PATH = catalogConnectPath('ditto');

/** The public web route that sends a phone to the app or to its store. */
export const HANDOFF_PATH = '/app/get';

const MAX_NEXT_LENGTH = 512;

export type MobileOs = 'ios' | 'android';

/**
 * The phone system of a user agent. iPadOS 13+ reports a Mac user agent, so an iPad in desktop
 * mode reads as `undefined` and gets the desktop page.
 */
export function detectMobileOs(userAgent: string): MobileOs | undefined {
  if (/android/i.test(userAgent)) {
    return 'android';
  }
  if (/iphone|ipad|ipod/i.test(userAgent)) {
    return 'ios';
  }
  return undefined;
}

/**
 * True when `next` is an app path we may send the reader to: under `/app/`, on this origin, and
 * not the handoff route itself.
 */
export function isSafeNext(next: string | null | undefined): next is string {
  if (next === null || next === undefined || next.length > MAX_NEXT_LENGTH) {
    return false;
  }
  if (!next.startsWith('/app/') || next.startsWith('//') || next.includes('\\')) {
    return false;
  }
  return next !== HANDOFF_PATH && !next.startsWith(`${HANDOFF_PATH}?`) && !next.startsWith(`${HANDOFF_PATH}/`);
}

export function googlePlayUrl(next?: string): string {
  const url = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;
  return isSafeNext(next) ? `${url}&referrer=${encodeURIComponent(next)}` : url;
}

export function storeUrl(os: MobileOs, next?: string): string {
  return os === 'ios' ? IOS_APP_STORE_URL : googlePlayUrl(next);
}

/** The link in the QR code and in the email: the app opens it, or the phone gets the store. */
export function buildHandoffUrl(origin: string, next?: string): string {
  const url = `${origin}${HANDOFF_PATH}`;
  return isSafeNext(next) ? `${url}?next=${encodeURIComponent(next)}` : url;
}

/**
 * The link for an "Open the app" button on a phone browser. iOS does not open a universal link
 * that is tapped on a page of the same domain, so iOS gets the app's own scheme. Android gets an
 * intent link: Chrome opens the app, or Google Play when the app is not installed.
 */
export function openAppUrl(os: MobileOs, host: string, scheme: string, next: string): string {
  if (os === 'ios') {
    return `${scheme}://${next.replace(/^\/app\//, '')}`;
  }
  const fallback = encodeURIComponent(googlePlayUrl(next));
  return `intent://${host}${next}#Intent;scheme=https;package=${ANDROID_PACKAGE};S.browser_fallback_url=${fallback};end`;
}
